import { useState } from 'react';
import { Banner, Button, Dialog, Textarea, useToast } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import {
  applyChangeRequest,
  rejectChangeRequest,
  useInvalidatePortal,
  type PortalChangeRequest,
} from '../portal-api';
import record from './record.module.css';
import styles from './portal.module.css';

export interface ChangeRequestsCardProps {
  contactId: string;
  requests: PortalChangeRequest[];
}

/**
 * „Profiländerungen" (no frame; Portal 21's counterpart): the customer's open change requests as
 * „<Feld>: <alt> → <neu> · <eingereicht>" with „Übernehmen" and „Ablehnen" (reason) →
 * `POST /portal-change-requests/:id/apply|reject`. The review task „Profiländerung prüfen: …" opens this record.
 */
export function ChangeRequestsCard({ contactId, requests }: ChangeRequestsCardProps) {
  const { toast } = useToast();
  const invalidate = useInvalidatePortal();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<PortalChangeRequest | null>(null);
  const [note, setNote] = useState('');
  const [noteError, setNoteError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);

  if (requests.length === 0) return null;

  async function apply(request: PortalChangeRequest) {
    setBusyId(request.id);
    try {
      const result = await applyChangeRequest(request.id);
      toast({ message: result.message ?? `Übernommen: ${request.fieldLabel}.` });
      await invalidate(contactId);
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setBusyId(null);
    }
  }

  async function reject() {
    if (!rejecting) return;
    if (note.trim() === '') {
      setNoteError('Bitte geben Sie einen Grund an.');
      return;
    }
    setBusyId(rejecting.id);
    setNoteError(null);
    setBanner(null);
    try {
      await rejectChangeRequest(rejecting.id, note.trim());
      toast({ message: `Abgelehnt: ${rejecting.fieldLabel}.` });
      setRejecting(null);
      await invalidate(contactId);
    } catch (error) {
      if (error instanceof ApiError && error.fields['note']) setNoteError(error.fields['note']);
      else setBanner(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <section className={record.card} aria-label="Profiländerungen">
      <div className={styles.headRow}>
        <span className={styles.eyebrow}>Profiländerungen</span>
        <span className={styles.headCount}>{requests.length} offen</span>
      </div>
      <ul className={styles.changeList}>
        {requests.map((request) => (
          <li key={request.id} className={styles.change}>
            <div className={styles.changeLine}>
              {request.fieldLabel}: <span className={styles.changeOld}>{request.oldValue}</span> →{' '}
              {request.newValue}
            </div>
            <div className={styles.changeMeta}>eingereicht {request.submittedLabel}</div>
            <div className={styles.changeActions}>
              <Button
                size="small"
                loading={busyId === request.id && rejecting === null}
                disabled={busyId !== null}
                onClick={() => void apply(request)}
              >
                Übernehmen
              </Button>
              <Button
                variant="secondary"
                size="small"
                disabled={busyId !== null}
                onClick={() => {
                  setNote('');
                  setNoteError(null);
                  setBanner(null);
                  setRejecting(request);
                }}
              >
                Ablehnen
              </Button>
            </div>
          </li>
        ))}
      </ul>

      <Dialog
        open={rejecting !== null}
        onClose={() => setRejecting(null)}
        width={560}
        tone="danger"
        title="Profiländerung ablehnen"
        subtitle={rejecting ? `${rejecting.fieldLabel}: ${rejecting.newValue}` : undefined}
        footer={
          <>
            <Button variant="secondary" disabled={busyId !== null} onClick={() => setRejecting(null)}>
              Abbrechen
            </Button>
            <Button
              variant="destructiveFilled"
              loading={busyId !== null && rejecting !== null}
              onClick={() => void reject()}
            >
              Ablehnen
            </Button>
          </>
        }
      >
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        <Textarea
          label="Grund"
          required
          rows={3}
          autoFocus
          value={note}
          error={noteError ?? undefined}
          onChange={(event) => setNote(event.target.value)}
        />
      </Dialog>
    </section>
  );
}
